// /submission/flow/continue?bd=…&roleid=…
//
// Where a visitor lands when they open a step past 1 in a browser that has no
// session for this flow: a "finish this later" email opened on another device,
// a cleared browser, a link a friend passed on, a bookmark. The URL knows the
// breakdown; the browser does not know who they are.
//
// Two kinds of people arrive here, and one screen serves both (decision D9,
// option C): someone with an account logs in to pick up their submission;
// someone new starts with their name, with the breakdown kept. It replaced
// "That link has expired / Back to sign up", which sent account holders to a
// signup form that then told them their email already had an account.
//
// It is also where the classic login hands a session back (Phase D, docs/28).
// "Log in" goes to the login with page=submission-{step}; the login returns here
// with k, the actor's session key. k is traded for the flow's token, leaves the
// address bar, and the actor resumes at that step, or the furthest one their
// submission has reached when that comes first. Their saved language wins once
// we know who they are.

import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import styled from "styled-components";
import i18next from "i18next";
import { iRootState } from "../../../store";
import SubmissionLayout from "../common/SubmissionLayout";
// @ts-ignore
import qs from "query-string";
import { getSubmissionLoginHint, isSubmissionAuthed } from "../../../helpers/submissionAuth";
import { handOffSignIn } from "../../../shared/api/submission/loginHandOff";
import { fallbackTo, hasFlowContext } from "../common/flowGuard";
import { applySignedInLanguage, ensureFlowLanguage } from "../common/flowLanguage";
import {
  flowLoginUrl,
  handOffKey,
  LAST_STEP,
  requestedStep,
  resumePath,
  withoutHandOff,
} from "../common/flowLogin";
import { parseSubmissionParams } from "../common/submissionParams";
import { forgetTabAccount } from "../common/tabAccount";
import { forgetMembershipAsked } from "../../../models/submissionStep4Model";
import { ISubmissionDraftDto } from "../../../shared/api/submission/types";
import { trackSubmissionEvent } from "../analytics";
import { reportEmailReturn, withoutEmailRef } from "../common/emailReturn";
import { membershipPrimary, membershipSecondary } from "../common/membershipButton";

interface Props extends StateProps, DispatchProps {
  history: any;
  location: any;
}

const SubmissionContinuePage: React.FC<Props> = ({
  history,
  location,
  submissionInfo,
  refreshGate,
  ensureDraft,
  resetSignup,
  resetFlow,
  resetStep4,
  setAccount,
}) => {
  // The search the visitor arrived with: the first replace below drops k from it.
  const [arrival] = useState<string>(location.search);
  const [signingIn, setSigningIn] = useState<boolean>(
    () => !!handOffKey(location.search) || isSubmissionAuthed()
  );
  const [handOffFailed, setHandOffFailed] = useState(false);

  useEffect(() => {
    // Back through a win-back or finish-later email: that email's return, once (emailReturn.ts).
    reportEmailReturn(arrival);
    // Without a breakdown there is nothing to continue: the classic signup.
    if (!hasFlowContext({ search: arrival })) {
      history.replace(fallbackTo(withoutHandOff(arrival)));
      return;
    }
    const k = handOffKey(arrival);
    if (k) {
      handOff(k);
      return;
    }
    // Signed in after all (another tab, a back-navigation): straight on.
    if (isSubmissionAuthed()) {
      resume();
      return;
    }
    showChoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showChoices = () => {
    // Reported on arrival: the email reference goes no further (Start with your name, the steps).
    const current = window.location.search;
    if (withoutEmailRef(current) !== current) {
      history.replace(`/submission/flow/continue${withoutEmailRef(current)}`);
    }
    setSigningIn(false);
    trackSubmissionEvent("sf_continue_viewed", {});
    // Names the breakdown, and sends a closed one to the closed page rather
    // than asking someone to log in to something that is gone.
    refreshGate({ history, search: withoutHandOff(arrival, ["page"]) });
  };

  // Back from the classic login: its session key becomes the flow's token, kept in this tab
  // only (loginHandOff.ts). An agent's or a casting account's key is refused like a bad one.
  const handOff = async (k: string) => {
    // The key leaves the address bar and the history at once, whatever happens next.
    history.replace(`/submission/flow/continue${withoutHandOff(arrival, ["page"])}`);
    trackSubmissionEvent("sf_login_returned", { step: requestedStep(arrival) });
    // Whoever is signed in here keeps the tab's token until the new sign-in is stored: an answer
    // of theirs landing meanwhile must not count for who came back (submissionStep4Model).
    forgetMembershipAsked();
    const clientId = await handOffSignIn(k);
    if (!clientId) {
      trackSubmissionEvent("sf_login_return_failed", {});
      // Whoever was signed in here before isn't who came back from the login: the tab is signed
      // out, so Start with your name starts a new account instead of resuming theirs.
      forgetTabAccount();
      await resetFlow();
      resetStep4();
      setHandOffFailed(true);
      showChoices();
      return;
    }
    // A fresh sign-in: nothing from an earlier visitor in this tab carries over. The draft
    // and their photos come back from the server.
    forgetTabAccount();
    resetSignup();
    resetStep4();
    setAccount({ clientId, accountDefaults: null });
    applySignedInLanguage();
    ensureFlowLanguage();
    await resume();
  };

  const resume = async () => {
    const { bd, roleid } = parseSubmissionParams(arrival);
    // The step's own checks still run there: a closed breakdown goes to the closed page.
    const draft = await ensureDraft({ breakdownId: bd, roleId: roleid });
    history.replace(`${resumePath(requestedStep(arrival), draft)}${withoutHandOff(arrival)}`);
  };

  // Back to the step asked for, or as far as the submission got. Whoever was signed in here
  // before is pre-filled.
  const arrivalLogin = qs.parse(arrival || "").login;
  const loginUrl = flowLoginUrl(
    arrival,
    requestedStep(arrival) || LAST_STEP,
    (typeof arrivalLogin === "string" && arrivalLogin) || getSubmissionLoginHint()
  );

  if (signingIn) {
    return (
      <SubmissionLayout>
        <Wrap aria-busy="true">
          <Context>{i18next.t("One moment…")}</Context>
        </Wrap>
      </SubmissionLayout>
    );
  }

  const onStart = () => {
    trackSubmissionEvent("sf_continue_start_new", {});
    history.replace(`/submission/flow/step/1${location.search}`);
  };

  const breakdownName = submissionInfo ? submissionInfo.breakdownName : "";

  return (
    <SubmissionLayout>
      <Wrap>
        <Title>{i18next.t("Continue your submission")}</Title>
        {handOffFailed && (
          <Notice role="status">{i18next.t("We couldn't sign you in. Please log in again.")}</Notice>
        )}
        {breakdownName && (
          <Context>
            {i18next.t("You're submitting to {{breakdownName}}.", { breakdownName })}
          </Context>
        )}

        <Block>
          <BlockTitle>{i18next.t("Already have an account?")}</BlockTitle>
          <BlockText>{i18next.t("Log in to pick up where you left off.")}</BlockText>
          <PrimaryLink
            href={loginUrl}
            onClick={() => trackSubmissionEvent("sf_continue_login", {})}
          >
            {i18next.t("Log in to continue")}
          </PrimaryLink>
        </Block>

        <Divider aria-hidden="true">
          <span>{i18next.t("or")}</span>
        </Divider>

        <Block>
          <BlockTitle>{i18next.t("New here?")}</BlockTitle>
          <BlockText>{i18next.t("Start with your name. It only takes a minute.")}</BlockText>
          <SecondaryButton onClick={onStart}>{i18next.t("Start with your name")}</SecondaryButton>
        </Block>
      </Wrap>
    </SubmissionLayout>
  );
};

const Wrap = styled.div`
  margin-top: 32px;
`;

const Title = styled.h1`
  font-size: 26px;
  font-weight: 700;
  color: #122640;
  margin: 0 0 8px;
  text-align: center;
`;

const Context = styled.p`
  font-size: 15px;
  line-height: 22px;
  color: #53627c;
  margin: 0 0 28px;
  text-align: center;
  overflow-wrap: anywhere;
`;

const Notice = styled.p`
  font-size: 14px;
  line-height: 20px;
  color: #122640;
  background-color: #f7f9fb;
  border: 1px solid #c9d3e0;
  border-radius: 5px;
  padding: 10px 12px;
  margin: 0 0 20px;
  text-align: center;
`;

const Block = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const BlockTitle = styled.h2`
  font-size: 16px;
  font-weight: 700;
  color: #122640;
  margin: 0;
`;

const BlockText = styled.p`
  font-size: 14px;
  line-height: 20px;
  color: #53627c;
  margin: 0 0 8px;
`;

const PrimaryLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  ${membershipPrimary}
  text-decoration: none;

  &,
  &:link,
  &:visited {
    color: #ffffff;
  }

  &:focus-visible {
    outline: 2px solid #0584d7;
    outline-offset: 2px;
  }
`;

const SecondaryButton = styled.button`
  min-height: 48px;
  ${membershipSecondary}

  &:focus-visible {
    outline: 2px solid #0584d7;
    outline-offset: 2px;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 24px 0;
  font-size: 13px;
  color: #8a97ab;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background-color: #e3e8ee;
  }
`;

interface StateProps {
  submissionInfo: iRootState["submissionFlowModel"]["submissionInfo"];
}

interface DispatchProps {
  refreshGate: (payload: { history: any; search?: string }) => Promise<any>;
  ensureDraft: (payload: { breakdownId: string | null; roleId: string | null }) => Promise<ISubmissionDraftDto | null>;
  resetSignup: () => void;
  resetFlow: () => Promise<any>;
  resetStep4: () => void;
  setAccount: (payload: { clientId: number; accountDefaults: null }) => void;
}

const mapStateToProps = (state: iRootState): StateProps => ({
  submissionInfo: state.submissionFlowModel.submissionInfo,
});

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  refreshGate: dispatch.submissionFlowModel.refreshGate,
  ensureDraft: dispatch.submissionSignupModel.ensureDraft,
  resetSignup: dispatch.submissionSignupModel.reset,
  resetFlow: dispatch.submissionSignupModel.resetFlow,
  resetStep4: dispatch.submissionStep4Model.reset,
  setAccount: dispatch.submissionSignupModel.setAccount,
});

export default connect(mapStateToProps, mapDispatchToProps)(SubmissionContinuePage);
